/**
 * htmlExport 内部辅助函数
 *
 * @module @home/utils/htmlExport/helpers
 */

/** AbortSignal 已取消时抛错 */
export function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw new DOMException('The operation was aborted.', 'AbortError');
  }
}

/** 等待下一帧，便于浏览器完成重排 */
export function nextAnimationFrame(): Promise<void> {
  return new Promise((resolve) => {
    window.requestAnimationFrame(() => resolve());
  });
}

/**
 * 等待 iframe 加载完成（含超时）
 *
 * @throws iframe 加载失败或超时
 */
export function waitForIframeLoad(
  iframe: HTMLIFrameElement,
  timeoutMs: number,
  signal?: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    throwIfAborted(signal);

    const onAbort = () => {
      cleanup();
      reject(new DOMException('The operation was aborted.', 'AbortError'));
    };

    const timeout = window.setTimeout(() => {
      cleanup();
      reject(new Error('iframe load timeout'));
    }, timeoutMs);

    const cleanup = () => {
      window.clearTimeout(timeout);
      iframe.onload = null;
      iframe.onerror = null;
      signal?.removeEventListener('abort', onAbort);
    };

    iframe.onload = () => {
      cleanup();
      resolve();
    };
    iframe.onerror = () => {
      cleanup();
      reject(new Error('iframe load error'));
    };
    signal?.addEventListener('abort', onAbort);
  });
}

/**
 * 等待 document 内所有 img 加载完成（含超时兜底）
 * abort 时 reject，与 waitForIframeLoad 行为一致
 */
export function waitForImages(
  doc: Document,
  timeoutMs: number,
  signal?: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      throwIfAborted(signal);
    } catch (error) {
      reject(error);
      return;
    }

    const images = doc.querySelectorAll('img');
    if (images.length === 0) {
      resolve();
      return;
    }

    let done = 0;
    let settled = false;

    const finish = (error?: Error) => {
      if (settled) {
        return;
      }
      settled = true;
      signal?.removeEventListener('abort', onAbort);
      window.clearTimeout(timeout);
      if (error) {
        reject(error);
      } else {
        resolve();
      }
    };

    const onAbort = () => {
      finish(new DOMException('The operation was aborted.', 'AbortError'));
    };

    const onDone = () => {
      done += 1;
      if (done >= images.length) {
        finish();
      }
    };

    const timeout = window.setTimeout(() => finish(), timeoutMs);
    signal?.addEventListener('abort', onAbort);

    images.forEach((img) => {
      if (img.complete) {
        onDone();
      } else {
        img.addEventListener('load', onDone);
        img.addEventListener('error', onDone);
      }
    });
  });
}

/**
 * 从 body 中取第一个有宽度的元素子节点作为截图根；找不到则用 body
 */
export function resolveContentRoot(bodyEl: HTMLElement): HTMLElement {
  const found = Array.from(bodyEl.children).find(
    (el) => el instanceof HTMLElement && el.scrollWidth > 0,
  ) as HTMLElement | undefined;
  return found || bodyEl;
}
